import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import TileGrid from 'components/TileGrid';
import Panel from 'components/Panel';
import ReactTimeout from 'react-timeout';
import Loader from 'components/Loader';
import {
  seriesVideoProcessor,
  clipsVideoProcessor,
  seriesCollectionVideoProcessor,
} from 'helpers/VideoProcessorHelper';
import { SHOW_DETAIL_RELATED_THUMBNAIL_IMAGE, renderImage } from 'helpers/ImageHelper';
import { helperGetApiHeaders, helperConvertApiRoute } from 'helpers/ApiHelper';
import { setHelmetTags } from 'helpers/HelmetHelper';
import {
  isClip,
  isEpisode,
  isStaticRender,
  canDisplayUpsell,
  isSeriesCollection,
} from 'helpers/ContentHelper';
import {
  SegmentActionLocations,
  setTealiumSourceShow,
  getSegmentModelMetadata,
  handleAnalytics,
  Events,
} from '@foxcorp/delta-web-analytics';
import { getTheme, webThemeIds } from 'helpers/ThemeHelper';
import { getTabName } from 'helpers/SeriesDetailHelpers';
import StyleTileGrid from './styles/TileGrid.module.scss';
import Style from './styles/VideosTab.css';
import seriesCollectionTileStyle from './styles/seriesCollectionVideoTiles.css';

const videoTileStyle = getTheme(webThemeIds.ContentDetail.VideosTab.VideoTile);

const getProcessor = ({ videoType }) => {
  if (isSeriesCollection()) return seriesCollectionVideoProcessor;

  switch (videoType) {
    case isEpisode(videoType):
      return seriesVideoProcessor;
    case isClip(videoType):
      return clipsVideoProcessor;
    default:
      return seriesVideoProcessor;
  }
};

class VideosTab extends Component {
  constructor(props) {
    super(props);
    this.state = { toggleLimit: 8 };
    this.renderTab = this.renderTab.bind(this);
  }

  renderTab(tab) {
    const {
      bookmarks,
      breakpoint,
      collectionCount,
      fetching,
      fetchingPage,
      isAuthenticated,
      isSeriesDetailPage,
      paginationItems,
      preauthorizedResources,
      showUpgradeCTA,
      tab: parentTab,
      tabSlug,
      verticalPosition,
    } = this.props;

    const { totalItems } = tab;
    const { toggleLimit } = this.state;
    const { global_moreSeriesDeeplinkButton: moreEpisodesText } = window.Language;
    const parentId = _.get(parentTab, 'id', null);
    const clips = _.get(tab, 'items.member', []);
    const videos = _.get(tab, 'member', clips);
    if (!videos || videos.length === 0) return null;
    const seo = _.get(tab, 'SEO');
    const videoType = _.get(videos, '0.videoType');
    const processor = getProcessor({ videoType, breakpoint });
    const tileStyle = isSeriesCollection() ? seriesCollectionTileStyle : videoTileStyle;

    const segmentPlaylistButtonAction = ({
      actionType,
      headline,
      horizontalPosition,
      title,
      subtitle,
      uiElement,
      originalItem,
    }) => {
      const modelMetaData = originalItem?.trackingData?.properties?.modelMetaData;
      handleAnalytics({
        event: Events.PLAYLIST_BUTTON_ACTION,
        payload: {
          page_action_type: actionType,
          page_collection_count: collectionCount,
          page_collection_title: headline,
          page_item_horizontal_position: horizontalPosition,
          page_item_title: title,
          page_item_subtitle: subtitle,
          page_item_vertical_position: verticalPosition - 1,
          page_action_location: SegmentActionLocations.DETAIL,
          page_ui_element: uiElement,
          page_recs_collection_model_id: modelMetaData?.modelId ?? ' ',
          page_recs_collection_request_id: modelMetaData?.requestId ?? ' ',
          page_recs_collection_result_set_id: modelMetaData?.resultSetId ?? ' ',
          page_recs_collection_source_id: modelMetaData?.sourceId ?? ' ',
          ...getSegmentModelMetadata(),
        },
      });
    };

    const onClick = params => {
      const { originalItem, titleText: subtitle, uiElement } = params;
      let currentTabName = getTabName({ tab });
      if (!currentTabName) currentTabName = getTabName({ tab: parentTab });
      const title = _.get(originalItem, 'seriesName');
      const seasonNumber = _.get(originalItem, 'seasonNumber');
      if (isSeriesDetailPage) {
        segmentPlaylistButtonAction({
          actionType: 'Watch Now',
          headline: seasonNumber ? `Season ${seasonNumber}` : '',
          title: _.get(originalItem, 'name', ''),
          subtitle: _.get(originalItem, 'description', ''),
          uiElement,
          originalItem,
        });
      }
      handleAnalytics({
        event: Events.MENU_ITEM_SELECTED,
        payload: {
          page_category_title: currentTabName,
          page_item_title: title,
          page_item_subtitle: subtitle,
        },
      });
      setTealiumSourceShow(true);
    };
    const flattenedPaginationItems = _.flatten(paginationItems);
    const shouldDisplayUpsellCTA =
      !isStaticRender() && canDisplayUpsell(preauthorizedResources) && !isClip(videoType);

    let videosList = [...videos, ...flattenedPaginationItems];
    videosList = videosList.map(
      item =>
        item &&
        Object.assign(
          {
            isAuthenticated,
            preauthorizedResources,
          },
          item,
        ),
    );

    const renderTileGrid = videosList.length > 0;
    const gridType = 'grid-5-4-2-1';

    const tileGridProps = {
      bookmarks,
      context: 'seriesDetail',
      items: videosList,
      isSeriesDetailPage,
      processor,
      gridType,
      renderImage: image =>
        renderImage({
          breakpoint,
          imageType: SHOW_DETAIL_RELATED_THUMBNAIL_IMAGE,
          ...image,
        }),
      segmentPlaylistButtonAction,
      tab: parentTab,
      tileDetailToggle: false,
      tileTheme: tileStyle,
      toggleLimit,
      usePlayButtonThin: true,
      showUpgradeCTA,
      onTileClick: onClick,
      onTitleClick: onClick,
      displayUpsellCTA: shouldDisplayUpsellCTA,
    };
    const loader = fetching || fetchingPage ? <Loader /> : null;

    const loadMore = () => {
      const isLoading = loader !== null;
      let toggleCount = toggleLimit;

      this.setState({ toggleLimit: (toggleCount += 16) });

      if (isLoading || !this.panel) return;
      this.panel.fetchNextPage(true);
    };

    const moreDisplay = totalItems - toggleLimit > 0 ? 'flex' : 'none';

    let tileGridComponent;
    if (renderTileGrid) {
      tileGridComponent = <TileGrid {...tileGridProps} />;
    }

    return (
      <div className={Style.episodesWrapper} id={tabSlug || parentId} data-tab-id={parentId}>
        {setHelmetTags(seo)}
        <div className={StyleTileGrid.container}>
          {tileGridComponent}
          <div
            className={StyleTileGrid.squaredButton}
            onClick={loadMore}
            style={{ display: moreDisplay }}
          >
            {moreEpisodesText} ({totalItems - toggleLimit})
          </div>
          <div className={StyleTileGrid.dividerContainer}>
            <div className={StyleTileGrid.divider} />
          </div>
          {loader}
        </div>
      </div>
    );
  }

  render() {
    const { accessToken, fetchingPage, preauthorizedResources, tab, url } = this.props;
    const panelId = _.get(tab, '@id', url);
    return (
      <Panel
        accessToken={accessToken}
        renderer={this.renderTab}
        panelData={tab}
        panelUrl={url}
        headerGenerator={helperGetApiHeaders}
        routeGenerator={helperConvertApiRoute}
        pageType="seriesDetail"
        panelId={panelId}
        propsToWatch={{ fetchingPage, preauthorizedResources }}
        data-test="web-series-detail"
        hasInfiniteScroll
        onRef={ref => {
          this.panel = ref;
        }}
      />
    );
  }
}

function mapStateToProps(state) {
  return {
    fetching: state.videosTab.fetching,
    nextPage: state.videosTab.nextPage,
    paginationItems: state.videosTab.paginationItems,
    fetchingPage: state.panel.fetching,
  };
}

VideosTab.defaultProps = {
  accessToken: null,
  breakpoint: null,
  collectionCount: null,
  fetching: false,
  fetchingPage: false,
  inline: {},
  isAuthenticated: false,
  isSeriesDetailPage: false,
  paginationItems: [],
  preauthorizedResources: [],
  showUpgradeCTA: false,
  tab: null,
  tabName: null,
  tabSlug: null,
  tabs: null,
  url: null,
  videos: [],
};

VideosTab.propTypes = {
  accessToken: PropTypes.string,
  bookmarks: PropTypes.shape({}),
  breakpoint: PropTypes.string,
  collectionCount: PropTypes.number,
  fetching: PropTypes.bool,
  fetchingPage: PropTypes.bool,
  inline: PropTypes.shape({}),
  isAuthenticated: PropTypes.bool,
  isSeriesDetailPage: PropTypes.bool,
  paginationItems: PropTypes.arrayOf(PropTypes.shape({})),
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  showUpgradeCTA: PropTypes.bool,
  tab: PropTypes.shape({}),
  tabName: PropTypes.string,
  tabSlug: PropTypes.string,
  tabs: PropTypes.arrayOf(PropTypes.shape({})),
  url: PropTypes.string,
  verticalPosition: PropTypes.number,
  videos: PropTypes.arrayOf(PropTypes.shape({})),
};

export default ReactTimeout(connect(mapStateToProps, {})(VideosTab));
